'use client';
// Markets TanStack Query Hooks
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { marketsApi, TradePayload } from '@/services/api';
import type {
  EventResponse,
  Market,
  MarketsQueryParams,
  PaginatedResponse,
  UserPnlResponse,
} from '@/types';
import { getEventType } from '@/utils';

// Query Keys Factory
export const MARKET_QUERY_KEYS = {
  all: ['markets'] as const,
  lists: () => [...MARKET_QUERY_KEYS.all, 'list'] as const,
  list: (params: MarketsQueryParams) => [...MARKET_QUERY_KEYS.lists(), params] as const,
  details: () => [...MARKET_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...MARKET_QUERY_KEYS.details(), id] as const,
  search: (query: string) => [...MARKET_QUERY_KEYS.lists(), 'search', query] as const,
  breaking: () => [...MARKET_QUERY_KEYS.all, 'breaking'] as const,
  userPnl: (address: string, interval?: string, fidelity?: string) =>
    [...MARKET_QUERY_KEYS.all, 'user-pnl', address, interval, fidelity] as const,
  ruleTranslation: (text: string, languageCode?: string) =>
    [...MARKET_QUERY_KEYS.all, 'rule-translation', text, languageCode] as const,
};

// Fetch all markets
export const useMarkets = (params?: MarketsQueryParams) => {
  return useQuery<Market[]>({
    queryKey: MARKET_QUERY_KEYS.list(params || {}),
    queryFn: async () => {
      // Pass params to API - it mimics the logic for filtering
      return marketsApi.getAll(params);
    },
    staleTime: Infinity,
  });
};

// Fetch infinite markets (for load more)
export const useMarketsInfinite = (params?: MarketsQueryParams, options?: any) => {
  const initialOffset = params?.offset || 0;
  return useInfiniteQuery<PaginatedResponse<EventResponse>>({
    queryKey: [...MARKET_QUERY_KEYS.list(params || {}), 'infinite'],
    queryFn: async ({ pageParam = initialOffset }) => {
      return marketsApi.getPaginated({ ...params, offset: pageParam as number });
    },
    initialPageParam: initialOffset,
    getNextPageParam: (lastPage) => {
      if (lastPage.hasNextPage) {
        return lastPage.page * lastPage.limit;
      }
      return undefined;
    },
    staleTime: Infinity,
    ...options,
  });
};

// Fetch single market
export const useMarket = (id: string) => {
  return useQuery<EventResponse>({
    queryKey: MARKET_QUERY_KEYS.detail(id),
    queryFn: async () => {
      return marketsApi.getById(id);
    },
    enabled: !!id,
    staleTime: Infinity,
  });
};

// Fetch breaking markets (biggest movers in 24h)
export const useBreakingMarkets = () => {
  return useQuery<Market[]>({
    queryKey: MARKET_QUERY_KEYS.breaking(),
    queryFn: async () => {
      return marketsApi.getBreaking();
    },
    staleTime: Infinity,
  });
};

// Create market mutation (Local-only mock)
export const useCreateMarket = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (marketData: Omit<Market, 'id'>) => {
      return { ...marketData, id: Math.random().toString(36).substr(2, 9) } as Market;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MARKET_QUERY_KEYS.lists() });
    },
  });
};

// Update market mutation (Local-only mock)
export const useUpdateMarket = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, market }: { id: string; market: Partial<Market> }) => {
      return { id, ...market } as Market;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: MARKET_QUERY_KEYS.lists() });
      queryClient.invalidateQueries({
        queryKey: MARKET_QUERY_KEYS.detail(variables.id),
      });
    },
  });
};

// Delete market mutation (Local-only mock)
export const useDeleteMarket = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {},
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MARKET_QUERY_KEYS.lists() });
    },
  });
};

// Trade mutation (Local-only mock)
export const useTrade = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ marketId, payload }: { marketId: string; payload: TradePayload }) => {
      return {
        id: 'mock-trade-id',
        marketId,
        ...payload,
        price: 0.5,
        createdAt: new Date().toISOString(),
      };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: MARKET_QUERY_KEYS.detail(variables.marketId),
      });
    },
  });
};

// Fetch and classify events
export const useEvents = () => {
  return useQuery({
    queryKey: ['events'],
    queryFn: async () => {
      const events = await marketsApi.getEvents();
      return events.map((event) => ({
        ...event,
        eventType: getEventType(event),
      }));
    },
    staleTime: 60000,
  });
};

// Fetch realtime prices for specific clobTokenIds
export const useMarketPrices = (tokenIds: string[], enabled: boolean = true) => {
  return useQuery({
    queryKey: [...MARKET_QUERY_KEYS.all, 'prices', tokenIds],
    queryFn: () => marketsApi.getMarketPrices(tokenIds),
    enabled: enabled && tokenIds.length > 0,
    refetchInterval: 60000,
    staleTime: 10000,
  });
};

// Fetch tick size for a specific token
export const useTickSize = (tokenId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: [...MARKET_QUERY_KEYS.all, 'tickSize', tokenId],
    queryFn: () => marketsApi.getTickSize(tokenId),
    enabled: enabled && !!tokenId,
    staleTime: Infinity,
  });
};

// Fetch order book for a specific token
export const useOrderBook = (
  tokenId: string,
  enabled: boolean = true,
  refetchInterval: number = 1000
) => {
  return useQuery({
    queryKey: [...MARKET_QUERY_KEYS.all, 'orderBook', tokenId],
    queryFn: () => marketsApi.getOrderBook(tokenId),
    enabled: enabled && !!tokenId,
    refetchInterval,
    staleTime: 1000,
  });
};
// Fetch user PNL and timeline
export const useUserPnl = (
  address: string,
  interval?: string,
  fidelity: string = '1d',
  options?: { enabled?: boolean }
) => {
  return useQuery<UserPnlResponse>({
    queryKey: MARKET_QUERY_KEYS.userPnl(address, interval || 'ALL', fidelity),
    queryFn: () => marketsApi.getUserPnl(address, interval, fidelity),
    enabled: !!address && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

// Fetch translated market rules
export const useMarketRuleTranslation = (text?: string, languageCode?: string) => {
  return useQuery({
    queryKey: MARKET_QUERY_KEYS.ruleTranslation(text || '', languageCode),
    queryFn: async () => {
      if (!text) return null;
      return marketsApi.translateMarketRule(text);
    },
    enabled: !!text && !!languageCode,
    staleTime: 1000 * 60 * 60 * 24,
  });
};
